import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ToastContainer, toast } from "react-toastify";
import ModeEditOutlinedIcon from "@mui/icons-material/ModeEditOutlined";
import RouteIcon from "@mui/icons-material/Route";
import { Box, Button, Card, CardContent, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, FormGroup, Grid, IconButton, InputLabel, MenuItem, Modal, Radio, RadioGroup, Select, TextField, Tooltip, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import UpdateIcon from "@mui/icons-material/Update";
import moment from "moment";
import Layout from "../../components/layout";
import ProjectAndUnitDetails from "./components/project-and-unit-details";
import {
	useAddOpportunitySiteVisitMutation,
	useFetchOpportunityCommentsQuery,
	useGetOpportunityDetailsQuery,
	useUpdateOpportunityCommentsMutation,
	useUpdateOpportunityMutation,
	useGetOpportunityCheckinMutation,
	useCreateOpportunityCheckinMutation,
	// useGetAvailableSlotsQuery
	useLazyGetAvailableSlotsQuery,
	useGetBookingProposalsForOpportunityCountQuery,
	useLazyGetBookingProposalsForOpportunityQuery,
	useLazyGetCustomerInsightQuestionnaireQuery,
} from "../../../features/api/opportunity-api-slice";
import { useAppSelector, useGetUserDetailsHooks, useRedirectToLogin } from "../../hooks";
import { USER_ROLES, API_CONSTANTS } from "../../constants";
import CustomerDetails from "./components/customer-details";
import { useGetUnitDetailsByIdQuery } from "../../../features/api/project-api-slice";
import { useParams } from "react-router-dom";
import MaterialModal from "../../components/modal";
import NotInterestedModalContent from "../../components/not-interested-modal-content";
import NavigationPath from "../../components/navigation-path";
import {
	CustomizedDivider,
	CustomizedLink,
	CustomizedText,
	SolidButton,
} from "../../components/styled-componnets";
import {
	AssigneeListModalContainer,
	ButtonContainer,
	CommentAndNotesCard,
	DetailsAndNotesEventsContainer,
	OpportunityDetailsAndComments,
	TitleAndButtonContainer,
	StyledButton,
	ConvertAgainButtonContainer,
} from "./styled-components";
import { OpportunityEditableStatuses, Status } from "../../constants/statuses";
import Colors from "../../utils/theme/color";
import { useGetSalesPersonListQuery } from "../../../features/api/master-api-slice";
import SiteVisitDetails from "../../components/site-visit-details";
import CheckoutCommentDialog from "../../components/checkout-comment-dialog";
import {
	YYYY_MM_DD_WITH_HYPHEN,
	convertTo24Hour,
	convertToSeconds,
} from "../../utils/date";
import ModifySalesStageModalContent from "./components/modify-sales-stage-modal";
import BookingComments from "../../components/booking-comments";
import { CommentsProps } from "../../utils/booking-utils";
import PreSalesSiteVisitUpdate from "./components/presales-site-visit-update";
import SiteVisitTable from "./components/site-visit-table";
import LostTable from "./components/lost-reason-table";
import LostDetails from './components/lost-details'
import { useCreateProposalMutation } from "../../../features/api/proposals-api-slice";
import FollowUpMessageModalContent from "./components/trigger-follow-up-message";
import ProjectAndUnitPreference from "../../components/project-and-unit-preference";
import { ModifySalesStageModal } from "./styled-components";
import MaterialButton from "../../components/button";
import dayjs from "dayjs";
import { CloseOutlined } from "@mui/icons-material";
import CustomerProfilingModal from "./components/customer-profiling-modal";
import MissingProfilingQuestionsDialog, { MissingProfilingQuestion } from "./components/missing-profiling-questions-dialog";

import { fetchClaudeQuickSummary } from '../../utils/gemini-summary';
import { getMissingCheckoutFields, showMissingCheckoutFieldsToast } from '../../utils/checkout-validation';
import OpportunityOverviewCard from './components/opportunity-overview-card';

const ERROR = "error";
const SUCCESS = "success";
const OPPORTUNITIES = "OPPORTUNITIES";
const SITE_VISIT_COMPLETED = "Site Visit Completed";
const ON_DISCUSSION = "On Discussion with Client";
const LOST = "Lost";
const BOOKING_CONFIRMED = "Booking Confirmed";
const AGREEMENT_COMPLETED = "Agreement Completed";
const REVISIT_DECLINED = "Revisit Declined";
const REVISIT_SCHEDULED = "Revisit Scheduled";
const REVISIT_COMPLETED = "Re-visit Completed";
const ALLOTMENT_COMPLETED = "Allotment Completed";
const BOOKING_CANCELLED = "Booking Cancelled";
const REVISIT_EXPIRED = "Revisit Expired";
const BOOKING_INITIATED = "Booking Initiated";
const PROPOSAL = "proposal";
const CUSTOMER_PROFILING_READ_ONLY_SALES_STAGES = new Set([
	"client not visited",
	"lost",
	"sales approval",
	"crm confirmation",
	"booking confirmed",
	"allotment complete",
	"allotment completed",
	"agreement complete",
	"agreement completed",
]);
const CUSTOMER_PROFILING_SOURCES_REQUIRING_PRESALES_AND_SALES = new Set([
	"walk in",
	"existing customer",
	"customer referral",
	"channel partner",
	"employee referral",
	"cp app",
]);

const isCustomerProfilingQuestionVisible = (
	questionScope: string,
	effectiveScope: string,
	source?: string
) => {
	const normalizedQuestionScope = String(questionScope || "").toLowerCase().trim();
	const normalizedEffectiveScope = String(effectiveScope || "").toLowerCase().trim();
	const normalizedSource = String(source || "").toLowerCase().trim();

	if (!normalizedEffectiveScope) {
		return true;
	}

	if (
		normalizedEffectiveScope === "sales" &&
		CUSTOMER_PROFILING_SOURCES_REQUIRING_PRESALES_AND_SALES.has(normalizedSource)
	) {
		return normalizedQuestionScope === "presales" || normalizedQuestionScope === "sales";
	}

	if (normalizedEffectiveScope === "sales") {
		return normalizedQuestionScope === "sales" || normalizedQuestionScope === "presales";
	}

	return normalizedQuestionScope === normalizedEffectiveScope;
};

const isCustomerProfilingQuestionAnswered = (question: any) => {
	const answerType = String(question?.answer_type || "").toLowerCase();
	const answer = question?.current || {};
	const otherOptionIds = new Set((question?.other_option_ids || []).map(String));
	const single = String(answer?.single || "");
	const multi: string[] = Array.isArray(answer?.multi) ? answer.multi.map(String) : [];
	const text = String(answer?.text || "");
	const otherData = String(answer?.other_data || "");
	const otherDataMap = answer?.other_data_map || {};

	if (answerType === "single" || answerType === "dropdown") {
		if (!single) return false;
		if (otherOptionIds.has(single)) return Boolean(otherData.trim());
		return true;
	}

	if (answerType === "multi") {
		if (!multi.length) return false;
		return multi.every((value: string) => {
			if (!otherOptionIds.has(value)) return true;
			return Boolean(String(otherDataMap?.[value] || "").trim());
		});
	}

	return Boolean(text.trim());
};

const getMissingMandatoryQuestions = (
	questions: any[],
	effectiveScope: string,
	source?: string
): MissingProfilingQuestion[] =>
	questions
		.filter((question: any) => {
			const questionScope = String(question?.counts_toward_scope || "").toLowerCase().trim();
			return (
				Number(question?.is_mandatory || 0) === 1 &&
				isCustomerProfilingQuestionVisible(questionScope, effectiveScope, source) &&
				!isCustomerProfilingQuestionAnswered(question)
			);
		})
		.map((question: any) => ({
			text: String(question?.question_text || question?.name || ""),
			section: String(question?.section_title || "Other"),
		}));

interface OpportunityDetailsTypes {
	name?: string;
	first_name?: string;
	last_name?: string;
	contact_mobile?: string;
	contact_email?: string;
	status?: Status;
	project?: string;
	allocated_sales_team?: string;
	unit?: string;
	modified?: string;
	custom_external_channel_partner?: string;
	customer_name?: string;
	sales_partner?: string;
	unit_type?: string;
	transaction_date?: string;
	budget_range?: string;
	employed_at?: string;
	source?: string;
	sales_stage?: string;
	pre_sales_owner?: string;
	lost_reasons?: { lost_reason: string }[];
	follow_up_date?: string;
	follow_up_time?: string;
	follow_up_comment?: string;
	order_lost_reason?: string;
	custom_rating?: string;
	custom_date?: string;
}

export interface UnitDetailsType {
	name?: string;
	bhk?: string;
	unit?: string;
	floor?: string;
	block?: string;
	total_cost?: string;
	super_builtup_area?: string;
	carpet_area?: string;
}
export const materialModalProps = {
	display: "flex",
	justifyContent: "center",
	alignItems: "center",
};

const bottomTabContainerStyle = {
	marginLeft: "3rem",
	width: "100%",
	justifyContent: "space-between",
	marginRight: "3rem",
};

const closeIconStyle = {
	m: 1,
	color: Colors.greyColor,
	alignSelf: "end",
	cursor: "pointer",
};

const assigneeStyle = {
	p: 2,
	pr: 15,
	pl: 15,
	textAlign: "center",
};

interface LostDetailsProps {
	opportunityId: string;
	open: boolean;
	onClose: () => void;
}

interface Assignee {
	date: string;
	sales_person: string;
}

interface Slot {
	sales_person: string;
	available_slots: string[];
}

interface Assignee {
	date: string;
	slots: Slot[];
}

// ------------------------
// ✅ Component Props
// ------------------------
interface SlotSelectorProps {
	assigneeList: Assignee[];
}

const saveButtonStyle = { alignSelf: "center", width: "30%" };

const OpportunityDetails = (props: any) => {
	const params = useParams();
	const { t } = useTranslation();
	const { redirectToLoginBasedOnResponse } = useRedirectToLogin();

	const { getUserToken, isCurrentUserGRE, isCurrentUserCP, isProjectHead, isProjectSalesHead, isProjectSalesUser } = useGetUserDetailsHooks();

	const navigate = useNavigate();
	const opportunityId = params?.id;

	const [opportunityDetails, setOpportunityDetails] =
		useState<OpportunityDetailsTypes>({});
	const [unitDetails, setUnitDetails] = useState<UnitDetailsType>({});

	const [showNoInterestedModal, setShowNotInterestedModal] =
		useState<boolean>(false);
	const [isBottomBarHidden, setIsBottomBarHidden] = useState<boolean>(false);
	const [isButtonLoading, setIsButtonLoading] = useState(false);
	const [isReAssignModalVisible, setReAssignModalVisible] = useState(false);
	const [isSiteVisitUpdateLoading, setIsSiteVisitUpdateLoading] =
		useState(false);
	const [isModifySalesStageModalVisible, setIsModifySalesStageModalVisible] =
		useState(false);
	const [isTriggerFollowUpModalVisible, setIsTriggerFollowUpModalVisible] =
		useState(false);
	const [comments, setComments] = useState<CommentsProps[]>([]);
	const [comment, setComment] = useState("");
	const [closureComment, setClosureComment] = useState("");
	const [assigneeList, setAssigneeList] = useState([]);
	const [selectedAssignee, setSelectedAssignee] = useState<string>();
	const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);
	const [isAddNoteLoading, setIsAddNoteLoading] = useState<boolean>(false);
	const [isCreateProposalLoading, setIsCreateProposalLoading] =
		useState<boolean>(false);
	const [isVewAllLostReasons, setIsViewAllLostReasons] =
		useState<boolean>(false);
	// Computed values for better control of button visibility
	const shouldShowRightButton = useMemo(() => {
		// Show button by default, only hide if explicitly GRE without other roles
		if (isCurrentUserGRE === true && !isCurrentUserCP && !isProjectSalesUser && !isProjectSalesHead && !isProjectHead) {
			return false;
		}
		return true; // Show for everyone else
	}, [isCurrentUserGRE, isCurrentUserCP, isProjectSalesUser, isProjectSalesHead, isProjectHead]);

	const shouldHideBottomBar = useMemo(() => {
		// Only hide if explicitly set to hidden OR if user is pure GRE
		if (isBottomBarHidden) return true;
		if (isCurrentUserGRE === true && !isCurrentUserCP && !isProjectSalesUser && !isProjectSalesHead && !isProjectHead) {
			return true;
		}
		return false; // Show for everyone else
	}, [isBottomBarHidden, isCurrentUserGRE, isCurrentUserCP, isProjectSalesUser, isProjectSalesHead, isProjectHead]);

	const isCustomerProfilingReadOnly = useMemo(() => {
		const salesStage = String(opportunityDetails?.sales_stage || "").toLowerCase().trim();
		return CUSTOMER_PROFILING_READ_ONLY_SALES_STAGES.has(salesStage);
	}, [opportunityDetails?.sales_stage]);

	const [isConvertAgainModalVisible, setIsConvertAgainModalVisible] =
		useState(false);
	const [checkInType, setCheckInType] = useState<string | null>(null);
	const [checking, setChecking] = useState(false);
	const [loadingStatus, setLoadingStatus] = useState<boolean>(false)
	const [statusType, setStatusType] = useState<string | null>(null)
	const [dateTime, setDateTime] = useState(null);
	const [actionType, setActionType] = useState<'Check In' | 'Check Out' | ''>('');
	const [dialogOpen, setDialogOpen] = useState(false);
	const [reportText, setReportText] = useState('');
	const [canCheckIn, setCanCheckIn] = useState(false);
	const [remainingHours, setRemainingHours] = useState<string>("0h 0m 0s");
	const [showLostDetails, setShowLostDetails] = useState(false);
	const [isCustomerProfilingOpen, setIsCustomerProfilingOpen] = useState(false);
	const [customerProfilingRequestKey, setCustomerProfilingRequestKey] = useState(0);
	const [customerProfilingProgress, setCustomerProfilingProgress] = useState<number | null>(null);
	const [pendingMandatoryCount, setPendingMandatoryCount] = useState(0);
	const [missingQuestions, setMissingQuestions] = useState<MissingProfilingQuestion[] | null>(null);
	const [profilingOpenedFromMissing, setProfilingOpenedFromMissing] = useState(false);
	const [opportunityOverview, setOpportunityOverview] = useState<any>(null);
	const [quickSummary, setQuickSummary] = useState<string>("");
	const [isQuickSummaryLoading, setIsQuickSummaryLoading] = useState(false);
	const [quickSummaryError, setQuickSummaryError] = useState<string>("");
	const summaryRetryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const wasProfilingOpen = useRef(false);
	const [selectedDate, setSelectedDate] = useState<string | null>(null);
	const [selectedPersons, setSelectedPersons] = useState<string>("");
	const [selectedSlot, setSelectedSlot] = useState<string>("");
	const [selectedSlotInfo, setSelectedSlotInfo] = useState(""); // e.g., "John Doe||13:00"
	const [assignMessage, setAssignMessage] = useState<string>("");
	const [reAssigLoading, setReAssigLoading] = useState<boolean>(false)
	const [gettingSlots, setGettingSlots] = useState<boolean>(false)

	// ✅ 1. ADD NEW STATE FOR MASKING
	const [maskDetail, setMaskDetail] = useState<boolean>(false);
	const [previousCheckInFound, setPreviousCheckInFound] = useState<boolean>(true)

	// Closure Rating Modal States
	const [isClosureRatingModalVisible, setIsClosureRatingModalVisible] = useState(false);
	const [selectedRating, setSelectedRating] = useState<string>("");
	const [followUpDate, setFollowUpDate] = useState<string>("");
	const [isClosureRatingLoading, setIsClosureRatingLoading] = useState(false);

	// Booking Proposals States
	const [bookingProposals, setBookingProposals] = useState<any[]>([]);
	const [showBookingProposalsModal, setShowBookingProposalsModal] = useState(false);

	// Booking Proposals Count and Data
	const { data: bookingProposalsCount } = useGetBookingProposalsForOpportunityCountQuery({
		userToken: getUserToken(),
		opportunityId: opportunityId,
	}, {
		skip: !Boolean(opportunityId),
		refetchOnMountOrArgChange: true,
	});

	const [getBookingProposals] = useLazyGetBookingProposalsForOpportunityQuery();

	// Handle click on booking proposals count to navigate
	const handleBookingProposalsClick = useCallback(async () => {
		if (!opportunityId) return;

		// If no booking proposals exist, show message instead of navigating
		if (!bookingProposalsCount?.data?.total_booking_proposals) {
			toast.info("No proposals created for this opportunity");
			return;
		}

		try {
			const response = await getBookingProposals({
				opportunityId,
				userToken: getUserToken(),
			}).unwrap();

			if (response?.status_code === 200 && response?.data?.booking_proposals) {
				const proposals = response.data.booking_proposals;

				if (proposals.length === 1) {
					// If only one proposal, navigate directly
					navigate(`/proposal/${proposals[0].name}`);
				} else if (proposals.length > 1) {
					// If multiple proposals, show selection modal
					setBookingProposals(proposals);
					setShowBookingProposalsModal(true);
				}
			}
		} catch (error) {
			console.error("Error fetching booking proposals:", error);
			toast.error("Failed to fetch booking proposals");
		}
	}, [opportunityId, bookingProposalsCount, getBookingProposals, getUserToken, navigate]);

	// Handle booking proposal selection from modal
	const handleBookingProposalSelect = useCallback((proposalName: string) => {
		navigate(`/proposal/${proposalName}`);
		setShowBookingProposalsModal(false);
	}, [navigate]);

	const handleLeftButton = () => {
		setShowLostDetails(true);
	};

	const handleCloseLostDetails = () => {
		setShowLostDetails(false);  // Hide the LostDetails modal
		refetchOpportunityDetails(); // ✅ Refetch updated opportunity details
	};


	const [projectAndUnitPreference, setProjectAndUnitPreference] = useState<{
		project?: string;
		unit?: string;
	}>({
		project: opportunityDetails?.project,
		unit: opportunityDetails?.unit,
	});

	const projectAndUnitPreferenceRef = useRef(projectAndUnitPreference);

	useEffect(() => {
		if (isConvertAgainModalVisible) return;

		const nextPreference = {
			project: opportunityDetails?.project,
			unit: opportunityDetails?.unit,
		};

		setProjectAndUnitPreference(nextPreference);
		projectAndUnitPreferenceRef.current = nextPreference;
	}, [
		isConvertAgainModalVisible,
		opportunityDetails?.project,
		opportunityDetails?.unit,
	]);

	const [getOpportunityCheckin] = useGetOpportunityCheckinMutation();
	const [createOpportunityCheckin, { isLoading }] = useCreateOpportunityCheckinMutation();
	const [getAvailableSlots, { isLoading: isGetAvailableSlotsLoading }] = useLazyGetAvailableSlotsQuery();


	const {
		data: getOpportunityDetailsResponse,
		isSuccess: isGetOpportunityDetailsSuccess,
		isError: isGetOpportunityDetailsFailed,
		error: getOpportunityDetailsError,
		refetch: refetchOpportunityDetails,
		isFetching: isGetOpportunityDetailsFetching,
	} = useGetOpportunityDetailsQuery({
		opportunityId,
		userToken: getUserToken(),
	});

	const {
		data: getUnitDetailResponse,
		isSuccess: isGetUnitDetailSuccess,
		isError: isGetUnitDetailFailed,
		error: getUnitDetailError,
		refetch: refetchUnitDetails,
	} = useGetUnitDetailsByIdQuery({
		unit: getOpportunityDetailsResponse?.data?.unit,
		userToken: getUserToken(),
	});

	const [
		updateOpportunity,
		{
			isSuccess: isUpdateOpportunitySuccess,
			isError: isUpdateOpportunityFailed,
			data: updateOpportunityResponse,
			error: updateOpportunityError,
		},
	] = useUpdateOpportunityMutation();

	const [
		addOpportunitySiteVisit,
		{
			isSuccess: isAddOpportunitySiteVisitSuccess,
			isError: isAddOpportunitySiteVisitFailed,
			data: addOpportunitySiteVisitResponse,
			error: addOpportunitySiteVisitError,
		},
	] = useAddOpportunitySiteVisitMutation();

	const {
		data: getSalesPersonListResponse,
		isSuccess: isGetSalesPersonListSuccess,
		isError: isGetSalesPersonListFailed,
		error: getSalesPersonListError,
		refetch: refetchSalesPersonList,
	} = useGetSalesPersonListQuery({
		userToken: getUserToken(),
	});
	const [
		updateOpportunityComments,
		{
			isSuccess: isUpdateOpportunityCommentsSuccess,
			isError: isUpdateOpportunityCommentsFailed,
			data: updateOpportunityCommentsResponse,
			error: updateOpportunityCommentsError,
		},
	] = useUpdateOpportunityCommentsMutation();

	const {
		data: getOpportunityCommentsResponse,
		isSuccess: isGetOpportunityCommentsSuccess,
		isError: isGetOpportunityCommentsFailed,
		error: getOpportunityCommentsError,
		refetch: refetchOpportunityComments,
	} = useFetchOpportunityCommentsQuery(
		{
			userToken: getUserToken(),
			opportunityId,
		},
		{ skip: !Boolean(opportunityId) }
	);

	const [
		createProposal,
		{
			isSuccess: isCreateProposalSuccess,
			isError: isCreateProposalFailed,
			data: createProposalResponse,
			error: createProposalError,
		},
	] = useCreateProposalMutation();
	const [getCustomerInsightQuestionnaire] = useLazyGetCustomerInsightQuestionnaireQuery();

	// Fetch and generate quick summary

	const fetchQuickSummary = useCallback(async (forceRegenerate = false) => {
		if (!opportunityId) return;

		const customerInsight = (opportunityDetails as any)?.customer_insight;
		// opportunityDetails not yet loaded — wait for the next effect trigger
		if (!customerInsight) return;

		// On initial load, use saved summary to avoid an unnecessary Claude call
		const savedSummary = (opportunityDetails as any)?.custom_pre_sales_summary;
		if (!forceRegenerate && savedSummary?.trim()) {
			setOpportunityOverview(savedSummary.trim());
			return;
		}

		setIsQuickSummaryLoading(true);
		setQuickSummaryError("");
		try {
			const response: any = await getCustomerInsightQuestionnaire({
				userToken: getUserToken(),
				docname: (opportunityDetails as any)?.customer_insight || "",
				contextDoctype: "Opportunity",
				contextMode: "view",
				contextDocname: opportunityId,
			}).unwrap();
			const message = response?.message || response?.data || {};
			const questions = Array.isArray(message?.questions) ? message.questions : [];
			// Build flat list of answered presales-scope questions only
			const answeredQuestions: Array<{ question: string; answer: string }> = [];
			questions.forEach((q: any) => {
				if (String(q?.counts_toward_scope || '').toLowerCase().trim() !== 'presales') return;
				if (!isCustomerProfilingQuestionAnswered(q)) return;

				const answerType = String(q?.answer_type || '').toLowerCase();
				const a = q?.current || {};

				const getOptionLabel = (optionValue: string) => {
					if (!optionValue) return "";
					const rawOptions = q.options || q.option_list || [];
					const opt = rawOptions.find((o: any) => {
						const val = String(o?.value || o?.id || o?.name || "");
						return val === optionValue;
					});
					if (opt) {
						return String(opt?.label || opt?.option_label || opt?.value || opt?.id || opt?.name || "");
					}
					return optionValue;
				};

				let answerText = '';
				if (answerType === 'single' || answerType === 'dropdown') {
					const label = getOptionLabel(a.single);
					answerText = a.other_data?.trim()
						? `${label}: ${a.other_data.trim()}`
						: label;
				} else if (answerType === 'multi') {
					const multiList = Array.isArray(a.multi) ? a.multi.map(String) : [];
					answerText = multiList.map((v: string) => {
						const label = getOptionLabel(v);
						const otherVal = (a.other_data_map?.[v] || '').trim();
						return otherVal ? `${label}: ${otherVal}` : label;
					}).join(', ');
				} else {
					answerText = a.text;
				}
				answeredQuestions.push({ question: q.question_text || q.name, answer: answerText });
			});
			if (!answeredQuestions.length) return;
			const overview = await fetchClaudeQuickSummary(answeredQuestions, getUserToken);
			setOpportunityOverview(overview);
			// Persist generated summary — save failure must NOT hide the already-displayed overview
			if (overview) {
				try {
					await updateOpportunity({
						payload: { ...opportunityDetails, custom_pre_sales_summary: overview },
						userToken: getUserToken(),
					}).unwrap();
					refetchOpportunityDetails();
				} catch {
					// Save failed (likely stale modified timestamp) — overview stays visible,
					// will be regenerated and saved on next page load
				}
			}
		} catch (err: any) {
			const msg = err?.data?.exception || err?.message || "";
			const isOverloaded = msg.includes("529") || msg.toLowerCase().includes("overloaded");
			if (isOverloaded) {
				// Auto-retry once after 8s silently
				if (!summaryRetryTimer.current) {
					summaryRetryTimer.current = setTimeout(() => {
						summaryRetryTimer.current = null;
						fetchQuickSummary();
					}, 8000);
				}
				// Second failure — give up silently, next page load will retry
			} else {
				setQuickSummaryError("Failed to generate summary.");
				setOpportunityOverview(null);
			}
		} finally {
			setIsQuickSummaryLoading(false);
		}
	}, [getCustomerInsightQuestionnaire, getUserToken, opportunityDetails, opportunityId, updateOpportunity, refetchOpportunityDetails]);

	// Initial load — use saved summary if available
	useEffect(() => {
		fetchQuickSummary(false);
		// eslint-disable-next-line
	}, [opportunityId, (opportunityDetails as any)?.customer_insight]);

	// Modal close — always regenerate so edits to presales answers are reflected
	useEffect(() => {
		if (isCustomerProfilingOpen) {
			wasProfilingOpen.current = true;
		} else if (wasProfilingOpen.current) {
			wasProfilingOpen.current = false;
			fetchQuickSummary(true);
		}
		// eslint-disable-next-line
	}, [isCustomerProfilingOpen]);

	// Fetch customer profiling progress for button
	const fetchCustomerProfilingProgress = useCallback(async () => {
		if (!opportunityId) return;
		try {
			const response: any = await getCustomerInsightQuestionnaire({
				userToken: getUserToken(),
				docname: (opportunityDetails as any)?.customer_insight || "",
				contextDoctype: "Opportunity",
				contextMode: "view",
				contextDocname: opportunityId,
			}).unwrap();
			const message = response?.message || response?.data || {};
			const percent = message?.scope_progress?.sales?.percent;
			setCustomerProfilingProgress(typeof percent === 'number' ? percent : null);
			const questions = Array.isArray(message?.questions) ? message.questions : [];
			const effectiveScope = String(message?.effective_scope || "").toLowerCase();
			setPendingMandatoryCount(
				getMissingMandatoryQuestions(questions, effectiveScope, opportunityDetails?.source).length
			);
		} catch (e) {
			setCustomerProfilingProgress(null);
			setPendingMandatoryCount(0);
		}
	}, [getCustomerInsightQuestionnaire, getUserToken, opportunityDetails, opportunityId]);

	useEffect(() => {
		fetchCustomerProfilingProgress();
		// Also refetch when modal closes (in case answers changed)
	}, [opportunityId, (opportunityDetails as any)?.customer_insight, isCustomerProfilingOpen === false]);

	useEffect(() => {
		if (isAddNoteLoading) {
			if (isUpdateOpportunityCommentsSuccess) {
				if (updateOpportunityCommentsResponse?.data) {
					setIsAddNoteLoading(false);
					toast(updateOpportunityCommentsResponse?.message, {
						type: SUCCESS,
					});
					setComment("");
				} else if (updateOpportunityCommentsResponse?.message) {
					setIsAddNoteLoading(false);
					toast(updateOpportunityCommentsResponse?.message, {
						type: ERROR,
					});
				}
			} else if (isUpdateOpportunityCommentsFailed) {
				redirectToLoginBasedOnResponse(
					updateOpportunityCommentsError,
					updateOpportunityCommentsResponse
				);
				setIsAddNoteLoading(false);
			}
		}
		// eslint-disable-next-line
	}, [
		setIsAddNoteLoading,
		isUpdateOpportunityCommentsSuccess,
		isUpdateOpportunityCommentsFailed,
		updateOpportunityCommentsResponse,
		updateOpportunityCommentsError,
		isAddNoteLoading,
	]);

	useEffect(() => {
		if (isGetOpportunityCommentsSuccess) {
			if (Array.isArray(getOpportunityCommentsResponse?.data)) {
				setComments(getOpportunityCommentsResponse?.data);
			} else if (getOpportunityCommentsResponse?.message) {
				toast(getOpportunityCommentsResponse?.message, {
					type: ERROR,
				});
				setComments([]);
			}
		} else if (isGetOpportunityCommentsFailed) {
			redirectToLoginBasedOnResponse(
				getOpportunityCommentsError,
				getOpportunityCommentsResponse
			);
			setComments([]);
		}
		// eslint-disable-next-line
	}, [
		getOpportunityCommentsResponse,
		isGetOpportunityCommentsSuccess,
		isGetOpportunityCommentsFailed,
		getOpportunityCommentsError,
	]);

	useEffect(() => {
		refetchOpportunityComments();
	}, [refetchOpportunityComments]);

	const toggleNotInterestedModal = useCallback(() => {
		setIsViewAllLostReasons(false);
		setShowNotInterestedModal(!showNoInterestedModal);
	}, [showNoInterestedModal]);

	useEffect(() => {
		if (isGetOpportunityDetailsSuccess) {
			if (
				getOpportunityDetailsResponse?.data &&
				getOpportunityDetailsResponse.status_code ===
				API_CONSTANTS.successStatusCode
			) {
				setOpportunityDetails(getOpportunityDetailsResponse?.data);
			} else if (getOpportunityDetailsResponse?.message) {
				toast(getOpportunityDetailsResponse?.message, {
					type: ERROR,
				});
			}
		} else if (isGetOpportunityDetailsFailed) {
			redirectToLoginBasedOnResponse(
				getOpportunityDetailsError,
				getOpportunityDetailsResponse
			);
		}
	}, [
		getOpportunityDetailsResponse,
		isGetOpportunityDetailsSuccess,
		isGetOpportunityDetailsFailed,
		redirectToLoginBasedOnResponse,
		getOpportunityDetailsError,
	]);

	useEffect(() => {
		if (isGetUnitDetailSuccess) {
			if (Array.isArray(getUnitDetailResponse?.data)) {
				setUnitDetails(getUnitDetailResponse.data[0]);
			} else if (getUnitDetailResponse?.message) {
				toast(getUnitDetailResponse?.message, {
					type: ERROR,
				});
				setUnitDetails({});
			}
		} else if (isGetUnitDetailFailed) {
			redirectToLoginBasedOnResponse(getUnitDetailError, getUnitDetailResponse);
			setUnitDetails({});
		}
	}, [
		getUnitDetailResponse,
		isGetUnitDetailSuccess,
		isGetUnitDetailFailed,
		redirectToLoginBasedOnResponse,
		getUnitDetailError,
	]);

	useEffect(() => {
		if (isButtonLoading) {
			if (isUpdateOpportunitySuccess) {
				if (
					updateOpportunityResponse?.data?.name &&
					updateOpportunityResponse?.status_code ===
					API_CONSTANTS.successStatusCode
				) {
					// toast(updateOpportunityResponse.message, {
					//   type: "success",
					// });
					setShowNotInterestedModal(false);
					setIsButtonLoading(false);
				} else if (updateOpportunityResponse?.message) {
					toast(updateOpportunityResponse?.message, {
						type: ERROR,
					});
					setIsButtonLoading(false);
				}
			} else if (isUpdateOpportunityFailed) {
				redirectToLoginBasedOnResponse(
					updateOpportunityError,
					updateOpportunityResponse
				);
				setIsButtonLoading(false);
			}
		}
	}, [
		updateOpportunityResponse,
		isUpdateOpportunitySuccess,
		isUpdateOpportunityFailed,
		updateOpportunityError,
		isButtonLoading,
	]);

	const isProposalConverted = useMemo(
		() => opportunityDetails?.status === Status.CONVERTED,
		[opportunityDetails?.status]
	);

	useEffect(() => {
		if (
			opportunityDetails?.status &&
			!OpportunityEditableStatuses.includes(opportunityDetails?.status) &&
			!shouldShowMarkAsLostButton()
		) {
			setIsBottomBarHidden(true);
		} else {
			setIsBottomBarHidden(false);
		}
	}, [opportunityDetails?.status, isCurrentUserGRE, isCurrentUserCP, opportunityDetails?.sales_stage]);


	useEffect(() => {
		if (isGetSalesPersonListSuccess) {
			if (Array.isArray(getSalesPersonListResponse?.message)) {
			} else if (getSalesPersonListResponse?.message) {
				toast(getSalesPersonListResponse?.message, {
					type: ERROR,
				});
				setAssigneeList([]);
			}
		} else if (isGetSalesPersonListFailed) {
			redirectToLoginBasedOnResponse(
				getSalesPersonListError,
				getSalesPersonListResponse
			);
			setAssigneeList([]);
		}
	}, [
		getSalesPersonListResponse,
		isGetSalesPersonListSuccess,
		t,
		isGetSalesPersonListFailed,
		getSalesPersonListError,
	]);

	useEffect(() => {
		refetchSalesPersonList();
		refetchOpportunityDetails();
		refetchUnitDetails();
	}, [refetchOpportunityDetails, refetchSalesPersonList, refetchUnitDetails]);

	useEffect(() => {
		if (isSiteVisitUpdateLoading) {
			if (isAddOpportunitySiteVisitSuccess) {
				if (addOpportunitySiteVisitResponse?.data === opportunityId) {
					toast(addOpportunitySiteVisitResponse.message, {
						type: "success",
					});
					setIsSiteVisitUpdateLoading(false);
				} else if (addOpportunitySiteVisitResponse?.message) {
					toast(addOpportunitySiteVisitResponse?.message, {
						type: ERROR,
					});
					setIsSiteVisitUpdateLoading(false);
				}
			} else if (isAddOpportunitySiteVisitFailed) {
				redirectToLoginBasedOnResponse(
					addOpportunitySiteVisitError,
					addOpportunitySiteVisitResponse
				);
				setIsButtonLoading(false);
			}
		}
	}, [
		addOpportunitySiteVisitResponse,
		isAddOpportunitySiteVisitSuccess,
		isAddOpportunitySiteVisitFailed,
		addOpportunitySiteVisitError,
		redirectToLoginBasedOnResponse,
	]);

	useEffect(() => {
		if (comment) {
			setIsButtonDisabled(false);
		} else {
			setIsButtonDisabled(true);
		}
	}, [comment]);

	useEffect(() => {
		if (isCreateProposalLoading) {
			if (isCreateProposalSuccess) {
				if (createProposalResponse?.data?.booking_proposal_id) {
					navigateToProposals(
						createProposalResponse?.data?.booking_proposal_id
					);
					toast(createProposalResponse.message, {
						type: "success",
					});
				} else if (createProposalResponse?.message) {
					toast(createProposalResponse?.message, {
						type: ERROR,
					});
				}
				setIsCreateProposalLoading(false);
			} else if (isCreateProposalFailed) {
				redirectToLoginBasedOnResponse(
					createProposalError,
					createProposalResponse
				);
				setIsCreateProposalLoading(false);
			}
		}
	}, [
		createProposalResponse,
		isCreateProposalSuccess,
		isCreateProposalFailed,
		createProposalError,
		isCreateProposalLoading,
	]);

	const lostReasons = useCallback(
		(reasons: { value: string; label: string }[]) =>
			reasons?.map((reason: { value: string; label: string }) => ({
				lost_reason: reason?.label,
			})),
		[]
	);

	const updateLostReason = async (
		reasons: { value: string; label: string }[],
		description: string
	) => {
		setIsButtonLoading(true);
		let payload = {
			...opportunityDetails,
			...{
				lost_reasons: lostReasons(reasons),
				status: Status.LOST,
				order_lost_reason: description,
			},
		};
		await updateOpportunity({ payload, userToken: getUserToken() }).unwrap();
	};

	const reAssign = async () => {
		if (!selectedPersons) {
			toast("Please select a Sales Person", {
				type: ERROR,
			});
			return; // ✅ Prevent further execution
		}

		setIsButtonLoading(true);
		setReAssigLoading(true)

		const payload = {
			...opportunityDetails,
			follow_up_date: selectedDate,
			allocated_sales_team: selectedPersons,
			follow_up_time: selectedSlot,
		};

		try {
			const response = await updateOpportunity({ payload, userToken: getUserToken() }).unwrap();
			console.log(response)
			if (response.status_code === 200) {
				toast.success(`Opportunity ${opportunityDetails?.first_name} has been reassigned to ${selectedPersons} at ${selectedSlot}`,
					{
						autoClose: 6000, // time in milliseconds (6000ms = 6 seconds)
					}
				);
			}
			toggleReAssignModal();
			setSelectedDate(null);
			setSelectedPersons("");
			setSelectedSlot("");
			setSelectedSlotInfo("")
		} catch (err) {
			console.error("Update failed", err);
			toast("Failed to update opportunity", { type: ERROR });
		} finally {
			setIsButtonLoading(false);
			setReAssigLoading(false)
		}
	};



	const navigateToOpportunityEdit = () =>
		navigate(`/opportunity/${opportunityId}/edit`);

	const toggleReAssignModal = useCallback(
		() => setReAssignModalVisible(!isReAssignModalVisible),
		[isReAssignModalVisible]
	);


	const updateAssignee = useCallback(async () => {
		const { modified, ...restOpportunityDetails } = opportunityDetails;
		const payload = {
			...restOpportunityDetails,
			allocated_sales_team: selectedAssignee,
		};
		toggleReAssignModal();
		setIsButtonLoading(true);
		await updateOpportunity({ payload, userToken: getUserToken() }).unwrap();
	}, [
		opportunityDetails,
		selectedAssignee,
		toggleReAssignModal,
		updateOpportunity,
		getUserToken,
	]);

	const onClickAssignee = useCallback(
		(assignee: string) => {
			if (opportunityDetails?.allocated_sales_team === assignee) {
				toast(`Already assigned to ${assignee}`, {
					type: "info",
				});
			} else setSelectedAssignee(assignee);
		},
		[opportunityDetails?.allocated_sales_team]
	);

	const renderModalContent = () => (
		<NotInterestedModalContent
			customerName={opportunityDetails?.first_name}
			handleClose={toggleNotInterestedModal}
			updateLostReason={updateLostReason}
			isButtonLoading={isButtonLoading}
			lostReasons={opportunityDetails?.lost_reasons}
			isLostReasonsViewable={isVewAllLostReasons}
			lostDetail={opportunityDetails?.order_lost_reason}
			id={opportunityId}
		/>
	);

	const handleDateClick = (date: string) => {
		if (selectedDate === date) {
			setSelectedDate(null);
			setSelectedPersons("");
			setSelectedSlot("");
		} else {
			setSelectedDate(date);
			setSelectedPersons("");
			setSelectedSlot("");
		}
	};

	const renderAssignees = () => (
		<AssigneeListModalContainer>
			<CloseIcon
				sx={closeIconStyle}
				fontSize="small"
				onClick={toggleReAssignModal}
			/>
			<CustomizedText
				fontSize={20}
				sx={{ fontWeight: 600, alignSelf: "center" }}
			>
				{"Assign" + " " + opportunityDetails?.first_name + " " + opportunityDetails?.last_name + " " + "to"}
			</CustomizedText>
			<Grid
				sx={{ margin: 'auto' }}
				container
				justifyContent={selectedDate ? "flex-start" : "flex-start"}
			>
				{gettingSlots ? (
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						style={{ margin: 'auto' }}
					>
						<CircularProgress size={28} color="primary" />
					</Box>
				) : (
					assigneeList?.map((assignee: Assignee, index: number) => {
						const isExpanded = selectedDate === assignee.date;
						if (selectedDate && !isExpanded) return null;
						return (
							<Grid item key={index} sx={{ overflowY: "auto" }}>
								<Card
									variant="outlined"
									sx={{
										position: "relative",
										borderColor: isExpanded ? "blue" : "grey.300",
										boxShadow: isExpanded ? 4 : 1,
										cursor: "pointer",
										transition: "0.3s",
										width: isExpanded ? "75vw" : "auto",
										margin: '5px'

									}}
									onClick={() => !isExpanded && handleDateClick(assignee.date)}
								>
									<CardContent>
										{isExpanded && (
											<IconButton
												onClick={(e) => {
													e.stopPropagation();
													setSelectedDate(null);
													setSelectedPersons("");
													setSelectedSlot("");
													setSelectedSlotInfo("")
												}}
												sx={{ position: "absolute", top: 8, right: 8 }}
											>
												<CloseOutlined
													// onClick={handleClose}
													style={{ cursor: "pointer", fontSize: 18 }}
												/>
											</IconButton>
										)}
										<CustomizedText
											fontSize={16}
											variant="h6"
											sx={{
												color: isExpanded ? "blue" : "black",
												display: isExpanded ? "none" : "block",
											}}
										>
											{dayjs(assignee.date).format('DD/MM/YYYY')}
										</CustomizedText>
										{isExpanded && (
											<Box
												mt={3}
												sx={{
													maxHeight: 400,
													overflowY: "auto",
													p: 2,
													backgroundColor: "#f9f9f9",
													borderRadius: 2,
													border: "1px solid #e0e0e0",
													margin: 'auto'
												}}
											>
												<CustomizedText fontSize={16} fontWeight={600} sx={{ mb: 2 }}>
													Select Sales Person & Slot
												</CustomizedText>

												<RadioGroup
													value={selectedSlotInfo}
													onChange={(e) => {
														const [salesPerson, slot] = e.target.value.split("||");
														setSelectedPersons(salesPerson);
														setSelectedSlot(`${slot}:00`);
														setSelectedSlotInfo(e.target.value); // ✅ set combined value
													}}
												>
													<Grid container spacing={2}>
														{assignee.slots.map((s: Slot, idx: number) => (
															<Grid
																key={idx}
																item
																xs={12}
																sm={12}
																md={6}
																lg={4}
															>
																<Box
																	sx={{
																		border: "1px solid #ccc",
																		borderRadius: 2,
																		padding: 2,
																		backgroundColor: "#fff",
																	}}
																>
																	<CustomizedText fontSize={15} fontWeight={500} sx={{ mb: 1 }}>
																		{s.sales_person}
																	</CustomizedText>
																	{s.available_slots.map((slot: string, slotIdx: number) => (
																		<FormControlLabel
																			key={slotIdx}
																			value={`${s.sales_person}||${slot}`} // ✅ combined value
																			control={<Radio size="small" />}
																			label={<CustomizedText fontSize={14}>{slot}</CustomizedText>}
																		/>
																	))}
																</Box>
															</Grid>
														))}
													</Grid>
												</RadioGroup>
												<Button
													variant="contained"
													color="primary"
													sx={{ mt: 3, width: "100%" }}
													onClick={(e) => {
														e.stopPropagation();
														reAssign();
													}}
													disabled={!selectedSlotInfo}
												>
													{/* Submit */}
													{reAssigLoading ? "Submitting..." : "Submit"}
												</Button>
											</Box>
										)}

									</CardContent>
								</Card>
							</Grid>
						);
					}))}
			</Grid>
		</AssigneeListModalContainer>
	);

	const navigateToOpportunities = useCallback(() => {
		navigate(`/${OPPORTUNITIES}`);
	}, [navigate]);

	const navigateToProposals = useCallback(
		(proposalId: string) => {
			navigate(`/${PROPOSAL}/${proposalId}`);
		},
		[navigate]
	);

	const toggleModifySalesStageModal = useCallback(
		() => setIsModifySalesStageModalVisible(!isModifySalesStageModalVisible),
		[isModifySalesStageModalVisible]
	);

	const onClickModifySalesStage = async (salesStage: string) => {
		setIsButtonLoading(true);
		toggleModifySalesStageModal();
		let payload = {
			...opportunityDetails,
			...{
				sales_stage: salesStage,
			},
		};
		await updateOpportunity({ payload, userToken: getUserToken() }).unwrap();
	};

	const addSiteVisitData = useCallback(
		(data: {
			siteVisitDate: string;
			duration: string;
			checkInTime: string;
			checkOutTime: string;
			comment: string;
		}) => {
			setIsSiteVisitUpdateLoading(true);
			const payload = {
				opportunity_id: opportunityId,
				site_visits: [
					{
						site_visit_date: Boolean(data?.siteVisitDate)
							? data?.siteVisitDate
							: moment(new Date()).format(YYYY_MM_DD_WITH_HYPHEN),
						site_visit_time: convertTo24Hour(data?.checkInTime),
						check_out_time: convertTo24Hour(data?.checkOutTime),
						duration: convertToSeconds(data?.duration),
						site_visit_comment: data?.comment,
					},
				],
			};
			addOpportunitySiteVisit({ payload, userToken: getUserToken() });
		},
		[addOpportunitySiteVisit, getUserToken, opportunityId]
	);

	const isOpportunityEditable = useMemo(
		() =>
			opportunityDetails?.status &&
			OpportunityEditableStatuses.includes(opportunityDetails?.status),
		[opportunityDetails?.status]
	);

	const onChangeComment = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setComment(event.target.value);
		},
		[]
	);

	const onClear = () => setComment("");

	const onClickAddNote = () => {
		setIsAddNoteLoading(true);
		const payload = {
			opportunity_id: opportunityId,
			comment,
		};
		updateOpportunityComments({ userToken: getUserToken(), payload });
	};

	const validateCustomerProfilingMandatoryFields = useCallback(async () => {
		if (!opportunityId) return false;

		try {
			const response: any = await getCustomerInsightQuestionnaire({
				userToken: getUserToken(),
				docname: (opportunityDetails as any)?.customer_insight || "",
				contextDoctype: "Opportunity",
				contextMode: "view",
				contextDocname: opportunityId,
			}).unwrap();

			const message = response?.message || response?.data || {};
			const questions = Array.isArray(message?.questions) ? message.questions : [];
			const effectiveScope = String(message?.effective_scope || "").toLowerCase();
			const missing = getMissingMandatoryQuestions(questions, effectiveScope, opportunityDetails?.source);

			if (missing.length) {
				setIsConvertAgainModalVisible(false);
				setMissingQuestions(missing);
				return false;
			}

			return true;
		} catch (error) {
			toast.error("Failed to validate customer profiling mandatory fields. Please try again.");
			return false;
		}
	}, [
		getCustomerInsightQuestionnaire,
		getUserToken,
		opportunityDetails,
		opportunityDetails?.source,
		opportunityId,
	]);

	const convertToProposal = useCallback(
		async (project?: string, unit?: string) => {
			const canCreateProposal = await validateCustomerProfilingMandatoryFields();
			if (!canCreateProposal) return;

			setIsCreateProposalLoading(true);
			const payload = {
				opportunity: opportunityId,
				project,
				unit,
			};
			createProposal({ userToken: getUserToken(), payload });
		},
		[createProposal, getUserToken, opportunityId, validateCustomerProfilingMandatoryFields]
	);

	const toggleConvertAgainModal = useCallback(() => {
		setIsConvertAgainModalVisible(!isConvertAgainModalVisible);
	}, [isConvertAgainModalVisible]);

	const onClickConvert = useCallback(() => {
		const isProjectOrUnitMissing = !opportunityDetails?.project || !opportunityDetails?.unit;

		// Ask for project and unit if the opportunity is missing either value, or for converted/existing proposals.
		if (
			isProjectOrUnitMissing ||
			isProposalConverted ||
			(bookingProposalsCount?.data?.total_booking_proposals > 0)
		) {
			toggleConvertAgainModal();
		} else {
			convertToProposal(opportunityDetails?.project, opportunityDetails?.unit);
		}
	}, [
		convertToProposal,
		isProposalConverted,
		bookingProposalsCount?.data?.total_booking_proposals,
		opportunityDetails?.project,
		opportunityDetails?.unit,
		toggleConvertAgainModal,
	]);

	const openProfilingForMissingQuestions = () => {
		setMissingQuestions(null);
		setProfilingOpenedFromMissing(true);
		setCustomerProfilingRequestKey((prev) => prev + 1);
		setIsCustomerProfilingOpen(true);
	};

	const toggleFollowUpModal = useCallback(
		() => setIsTriggerFollowUpModalVisible(!isTriggerFollowUpModalVisible),
		[isTriggerFollowUpModalVisible]
	);

	const shouldShowClosureRatingButton = () => {
		const allowedStages = [
			'Client Not Visited',
			'Revisit Scheduled',
			'Revisit Declined',
			'Revisit Expired',
			'On Discussion with Client',
			'Site Visit Completed',
			'Re-visit Completed'
		];
		return allowedStages.includes(opportunityDetails?.sales_stage || '');
	};

	const shouldShowMarkAsLostButton = () => {
		const allowedStages = [
			'Site Visit Completed',
			'Re-visit Completed',
			'Revisit Scheduled',
			'Client Not Visited',
			"Revisit Expired"
		];
		return allowedStages.includes(opportunityDetails?.sales_stage || '');
	};

	const shouldShowMarkAsNotVisitedButton = () => {
		const allowedStages = [
			'Revisit Scheduled',
			'Site Visit Scheduled',
		];
		return allowedStages.includes(opportunityDetails?.sales_stage || '');
	};

	const toggleClosureRatingModal = useCallback(() => {
		if (!isClosureRatingModalVisible) {
			// Pre-populate with existing data when opening modal
			setSelectedRating(opportunityDetails?.custom_rating || "");
			setFollowUpDate(opportunityDetails?.custom_date || "");
			setClosureComment("");
		}
		setIsClosureRatingModalVisible(!isClosureRatingModalVisible);
	}, [isClosureRatingModalVisible, opportunityDetails?.custom_rating, opportunityDetails?.custom_date]);

	const viewAllLostReason = () => {
		setIsViewAllLostReasons(true);
		setShowNotInterestedModal(!showNoInterestedModal);
	};

	const onSaveClosureRating = async () => {
		if (!selectedRating) {
			toast("Please select a rating", { type: ERROR });
			return;
		}
		if (!followUpDate) {
			toast("Please select a follow-up date", { type: ERROR });
			return;
		}
		if (!closureComment.trim()) {
			toast("Please enter a comment", { type: ERROR });
			return;
		}
		if (closureComment.trim().length < 25) {
			toast("Comment must be at least 25 characters long", { type: ERROR });
			return;
		}

		setIsClosureRatingLoading(true);
		const payload = {
			...opportunityDetails,
			custom_rating: selectedRating,
			custom_date: followUpDate,
		};

		try {
			// Update opportunity with rating and date
			const response = await updateOpportunity({ payload, userToken: getUserToken() }).unwrap();
			if (response.status_code === 200) {
				// Add comment via comments API
				const commentPayload = {
					opportunity_id: opportunityId,
					comment: closureComment.trim(),
				};
				await updateOpportunityComments({ userToken: getUserToken(), payload: commentPayload });

				toast.success("Closure rating updated successfully");
				setSelectedRating("");
				setFollowUpDate("");
				setClosureComment("");
				toggleClosureRatingModal();
				refetchOpportunityDetails();
			}
		} catch (err) {
			console.error("Update failed", err);
			toast("Failed to update closure rating", { type: ERROR });
		} finally {
			setIsClosureRatingLoading(false);
		}
	};

	const updateProjectAndUnit = useCallback(
		(details: { project: string; unitType: string; unit: string }) => {
			const nextPreference = {
				project: details?.project,
				unit: details?.unit,
			};

			setProjectAndUnitPreference(nextPreference);
			projectAndUnitPreferenceRef.current = nextPreference;
		},
		[]
	);

	useEffect(() => {
		if (opportunityDetails?.project) { // Changed condition to check for project instead
			const timer = setTimeout(() => {
				if (opportunityDetails?.allocated_sales_team) {
					fetchCheckinStatus();
				}
				getSlots(); // Always get slots if project exists
			}, 3000);

			return () => clearTimeout(timer); // cleanup if the component unmounts or dependency changes
		}
	}, [opportunityDetails?.allocated_sales_team, opportunityDetails?.project]);

	const fetchCheckinStatus = async () => {
		const today = moment().format("YYYY-MM-DD");
		const followUpDate = opportunityDetails?.follow_up_date;
		const UNMASKED_STAGES = [
			SITE_VISIT_COMPLETED,
			ON_DISCUSSION,
			BOOKING_INITIATED,
			AGREEMENT_COMPLETED,
			REVISIT_DECLINED,
			REVISIT_SCHEDULED,
			REVISIT_COMPLETED,
			ALLOTMENT_COMPLETED,
			BOOKING_CANCELLED,
			REVISIT_EXPIRED,
			LOST,
			BOOKING_CONFIRMED,
		];
		setLoadingStatus(true);
		try {
			const response: any = await getOpportunityCheckin({
				userToken: getUserToken(),
				payload: {
					opportunity_id: opportunityId,
					sales_person: opportunityDetails?.allocated_sales_team,
				},
			});
			const statusCode = response?.data?.status_code;
			console.log("checkInType", response?.data?.data?.data?.type)



			if (statusCode === 200) {
				const checkInData = response.data.data;
				const checkType = checkInData.type;

				setCheckInType(checkType);
				setDateTime(checkInData.time);
				setStatusType(checkType);

				// Mask if not (no check-in/out and sales_stage is Site Visit Completed)
				setMaskDetail(
					!(
						(!checkInType || checkInType === "") &&
						UNMASKED_STAGES.includes(opportunityDetails?.sales_stage || "")
					)
				);

				if (checkType === "Out") {
					const logoutTime = moment(checkInData.time);
					const now = moment();
					const diffInHours = now.diff(logoutTime, "hours", true);

					if (diffInHours >= 3) {
						setCanCheckIn(true);
						setRemainingHours("0h 0m 0s");
						console.log("✅ You can check in now.");
					} else {
						const remaining = 3 - diffInHours;
						setCanCheckIn(false);

						const remainingMs = remaining * 60 * 60 * 1000;
						const duration = moment.duration(remainingMs);
						const hrs = Math.floor(duration.asHours());
						const mins = duration.minutes();
						const secs = duration.seconds();

						const timeString = `${hrs}h ${mins}m ${secs}s left`;
						setRemainingHours(timeString);
						console.log(`⏳ You can check in after ${timeString}`);
					}
				}

			} else if (statusCode === 404) {
				if (followUpDate === today) {
					console.log("📭 No check-in record, but follow-up date matches today ✅");
					setCheckInType("Out");
					setCanCheckIn(true);
					setRemainingHours("0h 0m 0s");
				} else {
					console.log(`❌ No check-in record and follow-up date (${followUpDate}) ≠ today (${today})`);
					setCanCheckIn(false);
				}
				// If no check-in/out and sales_stage is Site Visit Completed, do not mask
				setMaskDetail(
					!(
						(!checkInType || checkInType === "") &&
						UNMASKED_STAGES.includes(opportunityDetails?.sales_stage || "")
					)
				);
				setPreviousCheckInFound(false);
			} else {
				console.warn("Unexpected response:", response);
			}

		} catch (error: any) {
			// Also handle 404 errors from network layer if needed
			if (error?.response?.status === 404) {
				// If no check-in/out and sales_stage is Site Visit Completed, do not mask
				setMaskDetail(
					!(
						(!checkInType || checkInType === "") &&
						UNMASKED_STAGES.includes(opportunityDetails?.sales_stage || "")
					)
				);
				setCheckInType("Out");
				setCanCheckIn(true);
				setRemainingHours("0h 0m 0s");
			} else {
				console.error("❌ Error fetching opportunity check-in:", error);
			}
		} finally {
			setLoadingStatus(false);
		}
	};


	const getSlots = async () => {
		setGettingSlots(true)
		try {
			const response: any = await getAvailableSlots({ userToken: getUserToken(), project: opportunityDetails?.project, source: opportunityDetails?.source })
			setAssigneeList(response?.data.data || []);
			setAssignMessage(response?.data.message || "");
		} catch (error) {
			console.error("Error fetching slots:", error);
		} finally {
			setGettingSlots(false)
		}
	}

	const validateCheckoutFields = () => {
		const missing = getMissingCheckoutFields(getOpportunityDetailsResponse?.data);
		if (missing.length === 0) return true;
		showMissingCheckoutFieldsToast(missing, opportunityId ?? "", () =>
			navigate(`/opportunity/${opportunityId}/edit`, { state: { openCheckout: true } })
		);
		return false;
	};

	// Edit Details sends us back with this hint when the user came from the missing-fields message
	const location = useLocation();
	const autoOpenCheckout = Boolean((location.state as { openCheckout?: boolean } | null)?.openCheckout);
	useEffect(() => {
		if (!autoOpenCheckout || isGetOpportunityDetailsFetching || loadingStatus || checkInType === null) return;
		if (checkInType === 'In' && validateCheckoutFields()) {
			setActionType('Check Out');
			setDialogOpen(true);
		}
		navigate(location.pathname, { replace: true, state: null });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [autoOpenCheckout, isGetOpportunityDetailsFetching, loadingStatus, checkInType]);

	const handleOpen = async (type: 'Check In' | 'Check Out') => {
		if (type === 'Check Out' && !validateCheckoutFields()) {
			return false;
		}
		setActionType(type);
		setChecking(true); // show loading text
		try {
			const payload = {
				for_opportunity: opportunityId,
				sales_person: opportunityDetails?.allocated_sales_team,
				checkin_type: type === 'Check In' ? 'In' : 'Out',
				comment: reportText
			};
			const res = await createOpportunityCheckin({ payload, userToken: getUserToken() }).unwrap();
			if (res.status_code === 200) {
				toast.success(res.message)
				setReportText('')
				fetchCheckinStatus()
				return true;
			}
			toast.error(res.message || "Something went wrong. Please try again.");
			return false;
		} catch (error) {
			console.error("Check-in/out failed:", error);
			toast.error("Something went wrong. Please try again.");
			return false;
		} finally {
			setChecking(false); // hide loading text
		}
	};

	const formatDateTime = (dateStr: string | null) => {
		return dateStr ? moment(dateStr).format("hh:mm A") : "";
	};

	const renderTitleAndButton = () => (
		<TitleAndButtonContainer>
			<Box>
				<NavigationPath
					initialPath={t("app_common.dashboard")}
					forwardPath={t("opportunity_details.details")}
					onClick={navigateToOpportunities}
				/>
				<CustomizedText fontSize={36} gutterBottom>
					{t("opportunity_details.title")}
				</CustomizedText>
				{(isProjectHead || isProjectSalesHead || isProjectSalesUser) && (
					<StyledButton
						sx={{
							pt: 0.5,
							pb: 0.5,
							mt: 0.5,
							backgroundColor: "#fdd835",
							color: "#054586ff",
							"&:hover": {
								backgroundColor: "#fdd835",
							},
						}}
						onClick={() => {
							setProfilingOpenedFromMissing(false);
							setCustomerProfilingRequestKey((prev) => prev + 1);
							setIsCustomerProfilingOpen(true);
						}}
					>
						{`Customer Profiling${typeof customerProfilingProgress === 'number' ? ` ${Math.round(customerProfilingProgress)}%` : ''}`}
						{pendingMandatoryCount > 0 && (
							<Box
								component="span"
								role="status"
								sx={{ ml: 1, px: 0.9, py: 0.1, borderRadius: "10px", backgroundColor: "#c62828", color: "#fff", fontSize: 11, lineHeight: 1.6 }}
							>
								{`${pendingMandatoryCount} pending`}
							</Box>
						)}
					</StyledButton>
				)}
			</Box>

			<ButtonContainer>
				{statusType === 'In' && (
					<Button
						sx={{
							pt: 0.5,
							pb: 0.5,
							mr: 5,
							textTransform: 'none',
							fontWeight: 500,
							borderColor: '#c0c0c0',
							color: '#333',
							backgroundColor: 'Yellow',
							'&:hover': {
								backgroundColor: 'Yellow',
							},
						}}>
						<span style={{ marginRight: '10px' }}>Last Check In At</span> {formatDateTime(dateTime)}
					</Button>
				)}

			</ButtonContainer>

		</TitleAndButtonContainer >
	);

	const renderPreSalesSiteVisitDetails = () => (
		<PreSalesSiteVisitUpdate
			date={opportunityDetails?.follow_up_date}
			time={opportunityDetails?.follow_up_time}
			comment={opportunityDetails?.follow_up_comment}
		/>
	);

	const renderSiteVisitTable = () => {
		if (!getOpportunityDetailsResponse?.data) return null;
		const siteVisitData = getOpportunityDetailsResponse.data.site_visit_table || [];
		// Always shown, even with no visits yet, so the Guidelines button is there on every opportunity
		return <SiteVisitTable data={siteVisitData} source={opportunityDetails?.source} />;
	};

	const renderLostReasonTable = () => {
		const siteVisitData = getOpportunityDetailsResponse?.data.custom_lost_reason_table || [];
		return siteVisitData.length > 0 ? (
			<LostTable data={siteVisitData} />
		) : null;
	};


	const renderOpportunityDetails = () => (
		<Box sx={{ width: "68%" }}>
			<CustomerDetails
				details={opportunityDetails}
				viewAllLostReasons={viewAllLostReason}
				maskDetail={maskDetail}
				loadingStatus={loadingStatus}
			/>
			{/* Opportunity Overview Card */}
			{isQuickSummaryLoading && (
				<Box sx={{ display: "flex", alignItems: "center", gap: 1, my: 2 }}>
					<CircularProgress size={18} />
					<Typography variant="body2" color="text.secondary">Generating overview...</Typography>
				</Box>
			)}
			{!isQuickSummaryLoading && quickSummaryError && (
				<Typography variant="body2" color="error" sx={{ my: 1 }}>{quickSummaryError}</Typography>
			)}
			{!isQuickSummaryLoading && !quickSummaryError && opportunityOverview && typeof opportunityOverview === 'string' && opportunityOverview.length > 0 && (
				<OpportunityOverviewCard summary={opportunityOverview} />
			)}
			<ProjectAndUnitDetails
				project={opportunityDetails?.project}
				unitDetails={unitDetails}
				opportunityId={opportunityId}
				status={opportunityDetails?.status}
				unitType={opportunityDetails?.unit_type}
				onEditClick={toggleConvertAgainModal}
				unit={opportunityDetails?.unit}
			/>
			{opportunityDetails?.follow_up_date && renderPreSalesSiteVisitDetails()}
			{!isCurrentUserGRE && isCurrentUserCP && renderSiteVisitDetails()}
			{renderSiteVisitTable()}
			{renderLostReasonTable()}
		</Box>
	);

	const renderComments = () => (
		<CommentAndNotesCard>
			<BookingComments
				commentList={comments}
				comment={comment}
				onClickAddNote={onClickAddNote}
				onChangeComment={onChangeComment}
				onClear={onClear}
				isButtonDisabled={isButtonDisabled}
				isAddNoteLoading={isAddNoteLoading}
				sx={{ bookingCommentsContainerStyle: { width: "100%" } }}
				isCloseIconRequired={false}
			/>
		</CommentAndNotesCard>
	);

	const renderContent = () => (
		<DetailsAndNotesEventsContainer>
			<Box sx={{ m: 2, width: "90%" }}>
				{renderTitleAndButton()}

				{/* All action buttons - keep in single line */}
				<Box sx={{
					display: 'flex',
					justifyContent: 'flex-end',
					flexWrap: 'nowrap',
					gap: 1,
					mb: 2,
					mt: 1,
					alignItems: 'center',
					width: '100%',
					overflowX: 'auto'
				}}>
					{/* Check In/Out Button Logic */}
					{(
						opportunityDetails?.sales_stage !== "Booking Confirmed" &&
						opportunityDetails?.sales_stage !== "Confirmed" &&
						opportunityDetails?.sales_stage !== "Client Not Visited" &&
						opportunityDetails?.sales_stage !== "Booking Confirmed" &&
						opportunityDetails?.sales_stage !== "Allotment Complete" &&
						opportunityDetails?.sales_stage !== "Agreement Completed" &&
						opportunityDetails?.sales_stage !== "Booking Initiated" &&
						(isProjectHead || isProjectSalesHead || isProjectSalesUser) && (
							<>
								{loadingStatus ? (
									<StyledButton
										sx={{ pt: 0.5, pb: 0.5, mr: 1 }}>
										<CircularProgress size={20} color="secondary" />
									</StyledButton>
								) : checkInType === 'In' ? (
									<StyledButton
										sx={{ pt: 0.5, pb: 0.5, mr: 1, background: '#ffa07a', border: '1px solid rgb(182,0,0,0.1)' }}
										onClick={() => {
											if (!validateCheckoutFields()) return;
											setActionType('Check Out');
											setDialogOpen(true);
										}}
									>
										{checking ? <CircularProgress size={20} color="secondary" /> : 'Check Out'}
									</StyledButton>
								) : (
									<Tooltip
										title={
											!canCheckIn
												? `⏳ You cannot check in now. Please wait another ${remainingHours} hour(s).`
												: ''
										}
										arrow
										disableHoverListener={canCheckIn}
									>
										<span>
											{canCheckIn && (
												<StyledButton
													sx={{
														pt: 0.5,
														pb: 0.5,
														mr: 1,
														background: '#98fb98',
														border: '1px solid rgb(0,182,0,0.1)',
													}}
													onClick={() => handleOpen('Check In')}
													disabled={checking}
												>
													{checking ? <CircularProgress size={20} color="secondary" /> : 'Check In'}
												</StyledButton>
											)}
										</span>
									</Tooltip>
								)}
							</>
						)
					)}

					{!isCurrentUserGRE &&
						!isCurrentUserCP &&
						shouldShowClosureRatingButton() &&
						renderClosureRatingButton()}

					{isOpportunityEditable &&
						opportunityDetails?.sales_stage === SITE_VISIT_COMPLETED &&
						renderFollowUpButton()}

					{isOpportunityEditable &&
						(isCurrentUserGRE && renderReassignButton())}

					{opportunityDetails?.status !== Status.CONVERTED &&
						!(opportunityDetails?.source === "CP App" && !isOpportunityEditable) && (
							<StyledButton
								sx={{ pt: 0.5, pb: 0.5 }}
								{...(isOpportunityEditable && {
									endIcon: (
										<ModeEditOutlinedIcon sx={{ color: Colors.actionColor }} />
									),
								})}
								onClick={navigateToOpportunityEdit}
							>
								{isOpportunityEditable
									? t("opportunity_details.edit_details")
									: t("opportunity_details.reopen")}
							</StyledButton>
						)}
				</Box>

				<OpportunityDetailsAndComments>
					{renderOpportunityDetails()}
					{renderComments()}
				</OpportunityDetailsAndComments>
			</Box>

			<CheckoutCommentDialog
				open={dialogOpen}
				onClose={() => setDialogOpen(false)}
				comment={reportText}
				onCommentChange={setReportText}
				submitting={checking}
				onSubmit={async () => {
					if (await handleOpen('Check Out')) {
						setDialogOpen(false);
					}
				}}
				details={{
					customerName: opportunityDetails?.customer_name,
					project: opportunityDetails?.project,
					opportunityId,
					source: opportunityDetails?.source,
					cpName: opportunityDetails?.sales_partner,
					checkInTime: dateTime,
				}}
			/>
		</DetailsAndNotesEventsContainer>
	);

	const renderReassignButton = () => (
		<SolidButton
			sx={{ pt: 0.5, pb: 0.5, mr: 1 }}
			fontSize={16}
			onClick={toggleReAssignModal}
		>
			{t("opportunity_details.reassign")}
		</SolidButton>
	);

	const renderSiteVisitDetails = () => (
		<SiteVisitDetails
			addSiteVisitData={addSiteVisitData}
			isButtonLoading={false}
			isAddSiteVisitRequired={isOpportunityEditable}
		/>
	);

	const renderModifyButton = () => (
		<StyledButton
			sx={{ pt: 0.5, pb: 0.5, mr: 1 }}
			endIcon={
				<RouteIcon
					sx={{ color: Colors.actionColor, transform: "rotate(90deg)" }}
					fontSize="small"
				/>
			}
			onClick={toggleModifySalesStageModal}
		>
			{t("opportunity_details.modify_sales_stage")}
		</StyledButton>
	);

	const renderModifySalesStage = () => (
		<ModifySalesStageModalContent
			handleClose={toggleModifySalesStageModal}
			currentSalesStage={opportunityDetails?.sales_stage}
			onClickModify={onClickModifySalesStage}
			isButtonLoading={isButtonLoading}
		/>
	);

	const renderFollowUpButton = () => (
		<StyledButton
			sx={{ pt: 0.5, pb: 0.5, mr: 1 }}
			endIcon={
				<UpdateIcon sx={{ color: Colors.actionColor }} fontSize="small" />
			}
			onClick={toggleFollowUpModal}
		>
			{t("opportunity_details.send_follow_up")}
		</StyledButton>
	);

	const renderFollowUpMessage = () => (
		<FollowUpMessageModalContent
			handleClose={toggleFollowUpModal}
			name={`${opportunityDetails?.first_name} ${opportunityDetails?.last_name}`}
			project={opportunityDetails?.project}
			salesExecutive={opportunityDetails?.allocated_sales_team}
		/>
	);

	const renderClosureRatingButton = () => (
		<StyledButton
			sx={{
				pt: 0.5,
				pb: 0.5,
				mr: 1,
				backgroundColor: '#fdd835',
				color: '#054586ff',
				'&:hover': {
					backgroundColor: '#fdd835',
				}
			}}
			onClick={toggleClosureRatingModal}
		>
			Closure Rating
		</StyledButton>
	);

	const renderClosureRatingModal = () => (
		<ModifySalesStageModal sx={{
			p: { xs: 2, sm: 3, md: 4 },
			borderRadius: 3,
			boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
			border: '1px solid #e0e0e0',
			width: { xs: '95%', sm: '90%', md: '80%', lg: '70%', xl: '60%' },
			maxWidth: { xs: '400px', sm: '500px', md: '600px', lg: '700px' },
			maxHeight: { xs: '90vh', sm: '85vh', md: '80vh' },
			overflow: 'auto',
			margin: 'auto'
		}}>
			<Box sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: { xs: 2, sm: 2.5, md: 3 }
			}}>
				<Box sx={{
					textAlign: 'center',
					pb: { xs: 1.5, md: 2 },
					borderBottom: '2px solid #f5f5f5',
					mb: { xs: 0.5, md: 1 }
				}}>
					<CustomizedText
						fontSize={24}
						sx={{
							fontSize: { xs: 20, sm: 22, md: 24 },
							fontWeight: 700,
							color: '#1976d2',
							textShadow: '0 1px 2px rgba(0,0,0,0.1)'
						}}
					>
						🎯 Closure Rating
					</CustomizedText>
					<Typography
						variant="body2"
						sx={{
							color: '#666',
							mt: 1,
							fontSize: { xs: '0.875rem', md: '1rem' }
						}}
					>
						Evaluate and track opportunity closure potential
					</Typography>
				</Box>

				<Box sx={{
					backgroundColor: '#fafafa',
					p: { xs: 2, md: 3 },
					borderRadius: 2,
					border: '1px solid #e0e0e0'
				}}>
					<Typography
						variant="subtitle1"
						sx={{
							mb: { xs: 1.5, md: 2 },
							fontWeight: 600,
							color: '#333',
							fontSize: { xs: '1rem', md: '1.125rem' }
						}}
					>
						📊 Customer Pulse *
					</Typography>
					<RadioGroup
						value={selectedRating}
						onChange={(e) => setSelectedRating(e.target.value)}
						row={window.innerWidth > 600}
						sx={{
							justifyContent: { xs: 'flex-start', sm: 'space-around' },
							flexDirection: { xs: 'column', sm: 'row' },
							gap: { xs: 1, sm: 0 }
						}}
					>
						<FormControlLabel
							value="Hot"
							control={<Radio sx={{ color: '#f44336', '&.Mui-checked': { color: '#f44336' } }} />}
							label={<span style={{ color: '#f44336', fontWeight: 'bold' }}>Hot</span>}
						/>
						<FormControlLabel
							value="Warm"
							control={<Radio sx={{ color: '#ff9800', '&.Mui-checked': { color: '#ff9800' } }} />}
							label={<span style={{ color: '#ff9800', fontWeight: 'bold' }}>Warm</span>}
						/>
						<FormControlLabel
							value="Cold"
							control={<Radio sx={{ color: '#2196f3', '&.Mui-checked': { color: '#2196f3' } }} />}
							label={<span style={{ color: '#2196f3', fontWeight: 'bold' }}>Cold</span>}
						/>
					</RadioGroup>
				</Box>

				<Box sx={{
					backgroundColor: '#f8f9fa',
					p: { xs: 2, md: 3 },
					borderRadius: 2,
					border: '1px solid #e0e0e0'
				}}>
					<Typography
						variant="subtitle1"
						sx={{
							mb: { xs: 1.5, md: 2 },
							fontWeight: 600,
							color: '#333',
							fontSize: { xs: '1rem', md: '1.125rem' }
						}}
					>
						📅 Follow Up Date *
					</Typography>
					<TextField
						type="date"
						value={followUpDate}
						onChange={(e) => setFollowUpDate(e.target.value)}
						inputProps={{
							min: moment().format('YYYY-MM-DD'),
							max: moment().add(7, 'days').format('YYYY-MM-DD')
						}}
						InputLabelProps={{
							shrink: true,
						}}
						fullWidth
						sx={{
							'& .MuiOutlinedInput-root': {
								backgroundColor: 'white',
							}
						}}
					/>
				</Box>

				<Box sx={{
					backgroundColor: '#f0f7ff',
					p: { xs: 2, md: 3 },
					borderRadius: 2,
					border: '1px solid #e3f2fd'
				}}>
					<Typography
						variant="subtitle1"
						sx={{
							mb: { xs: 1.5, md: 2 },
							fontWeight: 600,
							color: '#333',
							fontSize: { xs: '1rem', md: '1.125rem' }
						}}
					>
						💬 Comment * (Minimum 25 characters)
					</Typography>
					<TextField
						multiline
						rows={4}
						value={closureComment}
						onChange={(e) => setClosureComment(e.target.value)}
						placeholder="Enter detailed closure rating comment (minimum 25 characters)..."
						fullWidth
						sx={{
							backgroundColor: 'white',
							'& .MuiOutlinedInput-root': {
								'& fieldset': {
									borderColor: closureComment.trim().length < 25 && closureComment.trim().length > 0 ? '#f44336' : '#e0e0e0',
								},
								'&:hover fieldset': {
									borderColor: closureComment.trim().length < 25 && closureComment.trim().length > 0 ? '#f44336' : '#1976d2',
								},
								'&.Mui-focused fieldset': {
									borderColor: closureComment.trim().length < 25 && closureComment.trim().length > 0 ? '#f44336' : '#1976d2',
								},
							},
						}}
					/>
					<Typography
						variant="caption"
						sx={{
							mt: 0.5,
							color: closureComment.trim().length < 25 && closureComment.trim().length > 0 ? '#f44336' : '#666',
							fontSize: { xs: '0.7rem', md: '0.75rem' },
							fontWeight: 500
						}}
					>
						{closureComment.trim().length}/25 characters
					</Typography>
				</Box>

				<ConvertAgainButtonContainer sx={{
					flexDirection: { xs: 'column', sm: 'row' },
					gap: { xs: 1, sm: 0 },
					mt: { xs: 2, md: 3 }
				}}>
					<CustomizedLink sx={{
						mr: { xs: 0, sm: 1 },
						mb: { xs: 1, sm: 0 }
					}} onClick={toggleClosureRatingModal}>
						Cancel
					</CustomizedLink>
					<MaterialButton
						id={"save-closure-rating-button"}
						title="Save"
						sx={saveButtonStyle}
						onClick={onSaveClosureRating}
						disabled={!selectedRating || !followUpDate || !closureComment.trim() || closureComment.trim().length < 25}
						isLoading={isClosureRatingLoading}
					/>
				</ConvertAgainButtonContainer>
			</Box>
		</ModifySalesStageModal>
	);

	const renderBookingProposalsModal = () => (
		<ModifySalesStageModal sx={{ p: 3, maxWidth: '80%' }}>
			<Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', textAlign: 'center' }}>
				Select Booking Proposal
			</Typography>
			<Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
				{bookingProposals.map((proposal, index) => (
					<Card
						key={proposal.name}
						sx={{
							mb: 2,
							cursor: 'pointer',
							'&:hover': { backgroundColor: '#f5f5f5' },
							border: '1px solid #e0e0e0'
						}}
						onClick={() => handleBookingProposalSelect(proposal.name)}
					>
						<CardContent>
							<Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
								{proposal.name}
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
								Customer: {proposal.first_name} {proposal.last_name}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Project: {proposal.project} | Unit: {proposal.unit_name}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Status: {proposal.workflow_state}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Created: {moment(proposal.creation).format('DD MMM YYYY, hh:mm A')}
							</Typography>
							<Typography variant="body1" sx={{ mt: 1, fontWeight: 'bold', color: '#2e7d32' }}>
								Total Amount: ₹{proposal.total_amount?.toLocaleString()}
							</Typography>
						</CardContent>
					</Card>
				))}
			</Box>
			<ConvertAgainButtonContainer sx={{ mt: 2 }}>
				<CustomizedLink onClick={() => setShowBookingProposalsModal(false)}>
					Cancel
				</CustomizedLink>
			</ConvertAgainButtonContainer>
		</ModifySalesStageModal>
	);

	const renderProjectAndUnit = () => (
		<ModifySalesStageModal sx={{ p: 3 }}>
			<ProjectAndUnitPreference
				projectDetails={projectAndUnitPreference}
				onUpdate={updateProjectAndUnit}
				isUnitMandatory
			/>
			<ConvertAgainButtonContainer>
				<CustomizedLink sx={{ mr: 1 }} onClick={toggleConvertAgainModal}>
					{t("app_common.cancel")}
				</CustomizedLink>
				<MaterialButton
					id={"save-button"}
					title={t("opportunity_details.save")}
					sx={saveButtonStyle}
					onClick={() =>
						convertToProposal(
							projectAndUnitPreference.project,
							projectAndUnitPreference.unit
						)
					}
					disabled={
						!Boolean(projectAndUnitPreference?.project) ||
						!Boolean(projectAndUnitPreference?.unit)
					}
					isLoading={isCreateProposalLoading}
				/>
			</ConvertAgainButtonContainer>
		</ModifySalesStageModal>
	);

	return (
		<>
			<Layout
				previousCheckInFound={previousCheckInFound}
				children={renderContent()}
				rightButtonTitle={t("opportunity_details.create_booking_proposal")}
				rightButtonSupportText={
					pendingMandatoryCount > 0
						? `${pendingMandatoryCount} mandatory question${pendingMandatoryCount === 1 ? "" : "s"} pending in Customer Profiling`
						: ""
				}
				leftButtonTitle={
					shouldShowMarkAsLostButton() && opportunityDetails?.first_name
						? t("opportunity_details.query_not_interested", {
							name: opportunityDetails?.first_name,
						})
						: ""
				}
				hideBottomBar={false}
				styles={{
					...{ bottomTabContainer: bottomTabContainerStyle },
					...(isProposalConverted && !shouldShowMarkAsLostButton() && {
						buttonContainer: { display: "flex", justifyContent: "flex-end" },
					}),
				}}
				onClickLeftButton={handleLeftButton}
				isRightButtonRequired={true}
				onPressButton={onClickConvert}
				isRightButtonLoading={isCreateProposalLoading}
				isLeftButtonRequired={shouldShowMarkAsLostButton() || shouldShowMarkAsNotVisitedButton()}
				showMarkAsNotVisited={shouldShowMarkAsNotVisitedButton()}
				// Secondary button for booking proposals
				secondaryRightButtonTitle={
					bookingProposalsCount?.data?.total_booking_proposals > 0
						? `📄 ${bookingProposalsCount.data.total_booking_proposals} Booking Proposal${bookingProposalsCount.data.total_booking_proposals > 1 ? 's' : ''}`
						: "Go to Booking Proposal"
				}
				onClickSecondaryRightButton={handleBookingProposalsClick}
				isSecondaryRightButtonRequired={true}
			/>
			{showLostDetails && opportunityId && (
				<LostDetails
					opportunityId={opportunityId}
					open={showLostDetails}
					onClose={handleCloseLostDetails}
				/>
			)}
			<MaterialModal
				children={renderModalContent()}
				sx={{ ...materialModalProps, ...{ width: "60%" } }}
				open={showNoInterestedModal}
				onClose={toggleNotInterestedModal}
			/>
			<MaterialModal
				children={renderAssignees()}
				sx={materialModalProps}
				open={isReAssignModalVisible}
				onClose={toggleReAssignModal}
			/>
			<MaterialModal
				children={renderModifySalesStage()}
				sx={{ ...materialModalProps, ...{ width: "40%" } }}
				open={isModifySalesStageModalVisible}
				onClose={toggleModifySalesStageModal}
			/>
			<MaterialModal
				children={renderFollowUpMessage()}
				sx={{ ...materialModalProps, ...{ width: "40%" } }}
				open={isTriggerFollowUpModalVisible}
				onClose={toggleFollowUpModal}
			/>
			<MaterialModal
				children={renderProjectAndUnit()}
				sx={{ ...materialModalProps, ...{ width: "40%" } }}
				open={isConvertAgainModalVisible}
				onClose={toggleConvertAgainModal}
			/>
			<MaterialModal
				children={renderClosureRatingModal()}
				sx={{ ...materialModalProps, ...{ width: "40%" } }}
				open={isClosureRatingModalVisible}
				onClose={toggleClosureRatingModal}
			/>
			<MaterialModal
				children={renderBookingProposalsModal()}
				sx={{ ...materialModalProps, ...{ width: "60%" } }}
				open={showBookingProposalsModal}
				onClose={() => setShowBookingProposalsModal(false)}
			/>
			<CustomerProfilingModal
				open={isCustomerProfilingOpen}
				onClose={() => { setIsCustomerProfilingOpen(false); refetchOpportunityDetails(); }}
				userToken={getUserToken()}
				opportunityId={opportunityId}
				profileDocname={(opportunityDetails as any)?.customer_insight}
				source={opportunityDetails?.source}
				readOnly={isCustomerProfilingReadOnly}
				requestKey={customerProfilingRequestKey}
				initialOnlyMandatory={profilingOpenedFromMissing}
				initialOnlyUnanswered={profilingOpenedFromMissing}
			/>
			<MissingProfilingQuestionsDialog
				questions={missingQuestions}
				onClose={() => setMissingQuestions(null)}
				onComplete={openProfilingForMissingQuestions}
			/>
			<ToastContainer />
		</>
	);
};
export default OpportunityDetails;
